import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  OutlineButton,
  Badge,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableHeader,
  FlexContainer,
  Grid,
  ProgressBar
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { Project, FundRequest, ProcurementRecord, User } from '../../types';
import apiService from '../../services/api';
import { checkAuthStatus, clearAuthAndRedirect } from '../../utils/authUtils';
import { formatDate } from '../../utils/dateUtils';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ProjectHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  color: white;
  padding: 2rem;
  border-radius: var(--border-radius);
  margin-bottom: 2rem;
  
  h1 {
    margin: 0 0 0.5rem 0;
    font-size: 2rem;
  }
  
  p {
    margin: 0;
    opacity: 0.9;
  }
`;

const ProjectMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .label {
    font-weight: 500;
    opacity: 0.8;
  }
  
  .value {
    font-weight: 600;
  }
`;

const BudgetSection = styled.div`
  margin-bottom: 2rem;
`;

const BudgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const BudgetCard = styled(Card)`
  text-align: center;
  
  .amount {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary-color);
    margin: 0.5rem 0;
  }
  
  .label {
    color: var(--dark-gray);
    font-size: 0.9rem;
  }
`;

const CategoryBreakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const CategoryItem = styled.div`
  padding: 1rem;
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  .category-name {
    font-weight: 600;
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
  }
  
  .budget-section {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--light-gray);
  }
  
  .budget-amount {
    color: var(--dark-gray);
    font-size: 0.9rem;
  }
  
  .payments-section {
    margin-top: 0.5rem;
  }
  
  .payments-label {
    font-size: 0.8rem;
    color: var(--dark-gray);
    margin-bottom: 0.25rem;
  }
  
  .payments-amount {
    color: var(--primary-color);
    font-weight: 600;
    font-size: 0.9rem;
  }
`;

const StaffSection = styled.div`
  margin-bottom: 2rem;
`;

const StaffGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const StaffCard = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  padding: 1rem;
  
  .staff-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }
  
  .staff-name {
    font-weight: 600;
    color: var(--secondary-color);
  }
  
  .staff-role {
    color: var(--dark-gray);
    font-size: 0.9rem;
  }
  
  .staff-dates {
    color: var(--medium-gray);
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 2rem;
`;

const TabButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 2px solid var(--light-gray);
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--dark-gray)'};
  border: none;
  padding: 0.75rem 1rem;
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'var(--light-gray)'};
  }
`;

const TabContent = styled.div`
  min-height: 300px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--dark-gray);
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 80px;
  text-align: center;
`;

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [fundRequests, setFundRequests] = useState<FundRequest[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'funds' | 'staff'>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadProjectData = async () => {
      console.log('ProjectDetail: Loading project data, ID:', id);
      
      if (id && id !== 'undefined' && id !== 'null') {
        try {
          setLoading(true);
          setError(null);
          
          // Load project details (no authentication check)
          console.log('ProjectDetail: Loading project from API...');
          const projectResponse = await apiService.getProject(id);
          console.log('ProjectDetail: Project response:', projectResponse);
          
          if (projectResponse.success && projectResponse.data) {
            console.log('ProjectDetail: Setting project data:', projectResponse.data);
            setProject(projectResponse.data);
          } else {
            console.error('ProjectDetail: Invalid project response:', projectResponse);
            setError('Project not found or access denied');
            setProject(null);
          }
          
          // Load fund requests for this project
          try {
            console.log('ProjectDetail: Loading fund requests...');
            const fundRequestsResponse = await apiService.getFundRequests({ projectId: id });
            console.log('ProjectDetail: Fund requests response:', fundRequestsResponse);
            setFundRequests(fundRequestsResponse.data || []);
          } catch (error) {
            console.error('ProjectDetail: Error loading fund requests:', error);
            setFundRequests([]);
          }
          
        } catch (error: any) {
          console.error('ProjectDetail: Error loading project:', error);
          console.error('ProjectDetail: Error details:', {
            message: error.message,
            stack: error.stack,
            response: error.response
          });
          
          setError(error.message || 'Failed to load project. Please try again.');
          setProject(null);
        } finally {
          setLoading(false);
        }
      } else {
        console.error('ProjectDetail: Invalid project ID:', id);
        setError('Invalid project ID');
        setLoading(false);
        setProject(null);
      }
    };
    
    loadProjectData();
  }, [id]);

  if (loading) {
    return <div>Loading project details...</div>;
  }

  if (!project) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h3 style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>
              {error || 'Project Not Found'}
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              {error || 'The project you\'re looking for may have been deleted or you don\'t have permission to view it.'}
            </p>
            <p style={{ fontSize: '0.9rem', color: 'var(--dark-gray)', marginBottom: '1.5rem' }}>
              Please check your authentication status or contact your administrator.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <Link to="/projects">
                <Button>Back to Projects</Button>
              </Link>
              <Button 
                onClick={() => window.location.reload()}
                style={{ backgroundColor: 'var(--secondary-color)' }}
              >
                Retry
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In-Progress': return 'info';
      case 'Draft': return 'warning';
      case 'On-Hold': return 'secondary';
      default: return 'primary';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0
    }).format(amount);
  };



  const calculateTotalSpent = () => {
    return fundRequests
      .filter(request => request.status === 'Paid')
      .reduce((total, request) => total + request.amount, 0);
  };

  const calculateCategorySpent = (category: string) => {
    return fundRequests
      .filter(request => request.status === 'Paid')
      .reduce((total, request) => {
        return total + request.items
          .filter(item => item.category === category)
          .reduce((itemTotal, item) => itemTotal + item.totalPrice, 0);
      }, 0);
  };

  const calculateCategoryPayments = (category: string) => {
    return fundRequests
      .filter(request => request.status === 'Paid')
      .reduce((total, request) => {
        return total + request.items
          .filter(item => item.category === category)
          .reduce((itemTotal, item) => itemTotal + item.totalPrice, 0);
      }, 0);
  };

  const getBudgetProgress = () => {
    const totalBudget = project.budget.total;
    const totalSpent = calculateTotalSpent();
    return totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0;
  };

  const canEditProject = () => {
    return true; // Temporarily allow all users to edit projects
  };

  const getAssignedStaffDetails = () => {
    return project.assignedStaff.map(staff => {
      // Use the populated user data from the API response
      const userDetails = staff.userId as any; // Type assertion for populated user data
      return {
        ...staff,
        name: userDetails?.name || 'Unknown User',
        email: userDetails?.email || 'No email',
        role: userDetails?.role || 'Unknown Role'
      };
    });
  };

  const handleRecalculateIncome = async () => {
    if (!id || !project) return;
    
    try {
      const response = await apiService.recalculateProjectIncome(id);
      if (response.success) {
        // Update the project state with new total income
        setProject(prev => prev ? {
          ...prev,
          totalIncome: response.data.totalIncome
        } : null);
        
        // Show success message
        alert(`Income recalculated successfully! Total Income: ${response.data.totalIncome.toLocaleString()} TZS`);
      }
    } catch (error: any) {
      console.error('Error recalculating income:', error);
      alert('Failed to recalculate income: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div>
      <PageHeader>
        <FlexContainer gap="1rem">
          <Button onClick={() => navigate('/projects')}>← Back to Projects</Button>
        </FlexContainer>
        <FlexContainer gap="0.5rem">
          {canEditProject() && (
            <Button as={Link} to={`/projects/edit/${project._id || project.id}`}>
              Edit Project
            </Button>
          )}
          <OutlineButton>
            Export PDF
          </OutlineButton>
        </FlexContainer>
      </PageHeader>

      <ProjectHeader>
        <h1>{project.name}</h1>
        <p>{project.description}</p>
        <ProjectMeta>
          <MetaItem>
            <span className="label">Status:</span>
            <StatusBadge variant={getStatusVariant(project.status)}>
              {project.status}
            </StatusBadge>
          </MetaItem>
          <MetaItem>
            <span className="label">Type:</span>
            <span className="value">{project.type}</span>
          </MetaItem>
          <MetaItem>
            <span className="label">Location:</span>
            <span className="value">{project.location}</span>
          </MetaItem>
          <MetaItem>
            <span className="label">Start Date:</span>
            <span className="value">{formatDate(project.startDate)}</span>
          </MetaItem>
          {project.endDate && (
            <MetaItem>
              <span className="label">End Date:</span>
              <span className="value">{formatDate(project.endDate)}</span>
            </MetaItem>
          )}
          <MetaItem>
            <span className="label">Project Manager:</span>
            <span className="value">
              {project.assignedStaff?.find(staff => staff.role === 'Project Manager')?.name || 
               (typeof project.managerId === 'object' && project.managerId?.name 
                ? project.managerId.name 
                : project.manager) || 
               'Not assigned'}
            </span>
          </MetaItem>
        </ProjectMeta>
      </ProjectHeader>

      <BudgetSection>
        <h2>Budget Overview</h2>
        <BudgetGrid>
          <BudgetCard>
            <CardBody>
              <div className="label">Total Budget</div>
              <div className="amount">{formatCurrency(project.budget.total)}</div>
            </CardBody>
          </BudgetCard>
          <BudgetCard>
            <CardBody>
              <div className="label">Total Income</div>
              <div className="amount">{formatCurrency(project.totalIncome || 0)}</div>
              {project.totalIncome === 0 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--dark-gray)', marginTop: '0.5rem' }}>
                  <button 
                    onClick={handleRecalculateIncome}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: 'var(--primary-color)', 
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      fontSize: '0.8rem'
                    }}
                  >
                    Recalculate from payments
                  </button>
                </div>
              )}
            </CardBody>
          </BudgetCard>
          <BudgetCard>
            <CardBody>
              <div className="label">Total Spent</div>
              <div className="amount">{formatCurrency(calculateTotalSpent())}</div>
            </CardBody>
          </BudgetCard>
          <BudgetCard>
            <CardBody>
              <div className="label">Remaining</div>
              <div className="amount">{formatCurrency(project.budget.total - calculateTotalSpent())}</div>
            </CardBody>
          </BudgetCard>
          <BudgetCard>
            <CardBody>
              <div className="label">Budget Utilization</div>
              <div className="amount">{getBudgetProgress().toFixed(1)}%</div>
              <ProgressBar progress={getBudgetProgress()} />
            </CardBody>
          </BudgetCard>
        </BudgetGrid>

        <h3>Budget by Category</h3>
        <CategoryBreakdown>
          {[
            { name: 'Materials', amount: project.budget.materials },
            { name: 'Labor', amount: project.budget.labor },
            { name: 'Equipment', amount: project.budget.equipment },
            { name: 'Sub-Contracts', amount: project.budget.subContracts },
            { name: 'Others', amount: project.budget.others }
          ].map(({ name, amount }) => {
            const payments = calculateCategoryPayments(name);
            const progress = amount > 0 ? (payments / amount) * 100 : 0;
            
            return (
              <CategoryItem key={name}>
                <div className="category-name">{name}</div>
                <div className="budget-section">
                  <div className="budget-amount">
                    Budget: {formatCurrency(amount)}
                  </div>
                </div>
                <div className="payments-section">
                  <div className="payments-label">Fund Request Payments:</div>
                  <div className="payments-amount">
                    {formatCurrency(payments)}
                  </div>
                </div>
                <ProgressBar progress={progress} />
              </CategoryItem>
            );
          })}
        </CategoryBreakdown>
      </BudgetSection>

      <TabContainer>
        <TabButtons>
          <TabButton 
            active={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </TabButton>
          <TabButton 
            active={activeTab === 'funds'} 
            onClick={() => setActiveTab('funds')}
          >
            Fund Requests ({fundRequests.length})
          </TabButton>
          <TabButton 
            active={activeTab === 'staff'} 
            onClick={() => setActiveTab('staff')}
          >
            Assigned Staff ({project.assignedStaff.length})
          </TabButton>
        </TabButtons>

        <TabContent>
          {activeTab === 'overview' && (
            <Grid columns="1fr 1fr" gap="2rem">
              <Card>
                <CardHeader>
                  <h3>Project Timeline</h3>
                </CardHeader>
                <CardBody>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Start Date:</strong> {formatDate(project.startDate)}
                  </div>
                                     {project.endDate && (
                     <div style={{ marginBottom: '1rem' }}>
                       <strong>End Date:</strong> {formatDate(new Date(project.endDate))}
                     </div>
                   )}
                  <div>
                    <strong>Created:</strong> {formatDate(project.createdAt)}
                  </div>
                </CardBody>
              </Card>

              {project.retentionMoney && (
                <Card>
                  <CardHeader>
                    <h3>Retention Money</h3>
                  </CardHeader>
                  <CardBody>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Amount:</strong> {formatCurrency(project.retentionMoney.amount)}
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Percentage:</strong> {project.retentionMoney.percentage}%
                    </div>
                    <div>
                      <strong>Release Date:</strong> {project.retentionMoney.releaseDate ? formatDate(new Date(project.retentionMoney.releaseDate)) : 'Not set'}
                    </div>
                  </CardBody>
                </Card>
              )}
            </Grid>
          )}

          {activeTab === 'funds' && (
            <Card>
              <CardHeader>
                <FlexContainer>
                  <h3>Fund Requests</h3>
                  <Button as={Link} to={`/funds/new?projectId=${project._id || project.id}`}>
                    + New Request
                  </Button>
                </FlexContainer>
              </CardHeader>
              <CardBody>
                {fundRequests.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableHeader>ID</TableHeader>
                          <TableHeader>Requester</TableHeader>
                          <TableHeader>Amount</TableHeader>
                          <TableHeader>Status</TableHeader>
                          <TableHeader>Date</TableHeader>
                          <TableHeader>Actions</TableHeader>
                        </TableRow>
                      </TableHead>
                      <tbody>
                        {fundRequests.map((request) => (
                          <TableRow key={request.id}>
                            <TableCell>
                              <Link to={`/funds/${request.id}`}>{request.id}</Link>
                            </TableCell>
                            <TableCell>{request.requester}</TableCell>
                            <TableCell>{formatCurrency(request.amount)}</TableCell>
                            <TableCell>
                              <StatusBadge variant={getStatusVariant(request.status)}>
                                {request.status}
                              </StatusBadge>
                            </TableCell>
                            <TableCell>{formatDate(request.createdAt)}</TableCell>
                            <TableCell>
                              <OutlineButton as={Link} to={`/funds/${request.id}`}>
                                View
                              </OutlineButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <EmptyState>
                    <div className="icon">💰</div>
                    <p>No fund requests found for this project.</p>
                  </EmptyState>
                )}
              </CardBody>
            </Card>
          )}



          {activeTab === 'staff' && (
            <StaffSection>
              <StaffGrid>
                {getAssignedStaffDetails().map((staff) => (
                  <StaffCard key={staff.userId}>
                    <div className="staff-header">
                      <div>
                        <div className="staff-name">{staff.name}</div>
                        <div className="staff-role">{staff.role}</div>
                      </div>
                                             <StatusBadge variant={staff.approved ? 'success' : 'warning'}>
                         {staff.approved ? 'Approved' : 'Pending'}
                       </StatusBadge>
                     </div>
                     <div className="staff-dates">
                       Assigned: {formatDate(staff.assignedAt)}
                       {staff.approvedAt && ` - Approved: ${formatDate(new Date(staff.approvedAt))}`}
                     </div>
                  </StaffCard>
                ))}
              </StaffGrid>
              
              {project.assignedStaff.length === 0 && (
                <EmptyState>
                  <div className="icon">👥</div>
                  <p>No staff assigned to this project.</p>
                </EmptyState>
              )}
            </StaffSection>
          )}
        </TabContent>
      </TabContainer>

      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default ProjectDetail; 